var _react_refresh_temp_1, _react_refresh_temp_2, _react_refresh_temp_3;
var _react_refresh_temp_4;
_react_refresh_temp_4 = $RefreshSig$();
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { createSelector } from '@reduxjs/toolkit';
import { useTranslation } from 'react-i18next';
import { Track } from 'livekit-client';
import { Dialog, Transition } from '@headlessui/react';
import { toast } from 'react-toastify';
import copy from 'copy-text-to-clipboard';
import { useAppDispatch, useAppSelector } from '../../store';
import { updateReceivedInvitationFor } from '../../store/slices/breakoutRoomSlice';
import { useJoinRoomMutation } from '../../store/services/breakoutRoomApi';
import { updateIsActiveWebcam, updateIsMicMuted, updateVirtualBackground, } from '../../store/slices/bottomIconsActivitySlice';
import { updateSelectedVideoDevice } from '../../store/slices/roomSettingsSlice';
import { JoinBreakoutRoomReq } from '../../helpers/proto/plugnmeet_breakout_room_pb';
const receivedInvitationForSelector = createSelector(_react_refresh_temp_1 = (state) => state.breakoutRoom, (breakoutRoom) => breakoutRoom.receivedInvitationFor);
$RefreshReg$(_react_refresh_temp_1, "receivedInvitationForSelector$createSelector");
const BreakoutRoomInvitation = ({ currentRoom, }) => {
    _react_refresh_temp_4();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const receivedInvitationFor = useAppSelector(receivedInvitationForSelector);
    const [joinRoom, { isLoading, data }] = useJoinRoomMutation();
    const [joinLink, setJoinLink] = useState('');
    const [copyText, setCopyText] = useState(t('breakout-room.copy').toString());
    const [token, setToken] = useState('');
    const closeLocalTracks = useCallback(() => {
        currentRoom.localParticipant.tracks.forEach(async (publication) => {
            if (!publication.track) {
                return;
            }
            if (publication.track.source === Track.Source.Camera) {
                currentRoom.localParticipant.unpublishTrack(publication.track, true);
                dispatch(updateIsActiveWebcam(false));
                dispatch(updateSelectedVideoDevice(''));
                dispatch(updateVirtualBackground({
                    type: 'none',
                }));
            }
            else if (publication.track.source === Track.Source.Microphone) {
                if (!publication.isMuted) {
                    await publication.track.unmute();
                    dispatch(updateIsMicMuted(true));
                }
            }
        });
    }, [currentRoom.localParticipant, dispatch]);
    useEffect(() => {
        var _a;
        if (!isLoading && data) {
            if (!data.status) {
                toast(t(data.msg), {
                    type: 'error',
                });
                return;
            }
            setToken((_a = data.token) !== null && _a !== void 0 ? _a : '');
        }
    }, [isLoading, data]);
    useEffect(() => {
        if (token !== '') {
            const searchParams = new URLSearchParams(window.location.search);
            searchParams.set('access_token', token);
            const url = location.protocol +
                '//' +
                location.host +
                window.location.pathname +
                '?' +
                searchParams.toString();
            const opened = window.open(url, '_blank');
            setJoinLink(url);
            if (!opened) {
                setJoinLink(url);
                return;
            }
            dispatch(updateReceivedInvitationFor(''));
            // we should disable running tracks
            closeLocalTracks();
        }
    }, [token]);
    const closeModal = () => {
        dispatch(updateReceivedInvitationFor(''));
        // we should disable running tracks
        closeLocalTracks();
    };
    const join = () => {
        joinRoom(new JoinBreakoutRoomReq({
            breakoutRoomId: receivedInvitationFor,
            userId: currentRoom.localParticipant.identity,
        }));
    };
    const copyUrl = () => {
        copy(joinLink);
        setCopyText(t('breakout-room.copied').toString());
    };
    const renderModal = () => {
        return (_jsx(_Fragment, { children: _jsx(Transition, { appear: true, show: receivedInvitationFor !== '', as: Fragment, children: _jsx(Dialog, { as: "div", className: "fixed inset-0 z-[9999] overflow-y-auto", onClose: () => false, static: false, children: _jsxs("div", { className: "min-h-screen px-4 text-center flex items-end justify-end", children: [_jsx(Transition.Child, { as: Fragment, enter: "ease-out duration-300", enterFrom: "opacity-0", enterTo: "opacity-100", leave: "ease-in duration-200", leaveFrom: "opacity-100", leaveTo: "opacity-0", children: _jsx(Dialog.Overlay, { className: "fixed inset-0 bg-black opacity-30" }) }), _jsx("span", { className: "inline-block h-screen align-middle", "aria-hidden": "true", children: "\u200B" }), _jsx(Transition.Child, { as: Fragment, enter: "ease-out duration-300", enterFrom: "opacity-0 scale-95", enterTo: "opacity-100 scale-100", leave: "ease-in duration-200", leaveFrom: "opacity-100 scale-100", leaveTo: "opacity-0 scale-95", children: _jsxs("div", { className: "inline-block w-max h-full p-6 my-4 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-darkPrimary shadow-xl rounded-lg", children: [_jsxs("button", { className: "close-btn absolute top-8 right-6 w-[25px] h-[25px] outline-none", type: "button", onClick: () => closeModal(), children: [_jsx("span", { className: "inline-block h-[1px] w-[20px] bg-primaryColor dark:bg-darkText absolute top-0 left-0 rotate-45" }), _jsx("span", { className: "inline-block h-[1px] w-[20px] bg-primaryColor dark:bg-darkText absolute top-0 left-0 -rotate-45" })] }), _jsx(Dialog.Title, { as: "h3", className: "text-base font-medium leading-6 text-gray-900 dark:text-darkText text-left mb-2", children: t('breakout-room.invitation-title') }), _jsx("hr", {}), _jsxs("div", { className: "mt-2", children: [_jsx("span", { className: "text-black dark:text-darkText text-sm", children: t('breakout-room.invitation-msg') }), joinLink !== '' ? (_jsxs("div", { className: "invite-link", children: [_jsx("label", { className: "text-black dark:text-darkText text-sm", children: t('breakout-room.join-text-label') }), _jsx("input", { type: "text", readOnly: true, value: joinLink, className: "inline-block outline-none border border-solid rounded p-1 h-7 text-sm mx-1 bg-transparent dark:text-darkText dark:border-darkText" }), _jsx("button", { onClick: copyUrl, className: "text-center py-1 px-3 text-xs transition ease-in bg-primaryColor hover:bg-secondaryColor text-white font-semibold rounded-lg", children: copyText })] })) : null, _jsx("div", { className: "button-section flex items-center justify-start", children: _jsx("button", { className: "text-center py-1 px-3 mt-1 text-xs transition ease-in bg-primaryColor hover:bg-secondaryColor text-white font-semibold rounded-lg", onClick: join, children: t('breakout-room.join') }) })] })] }) })] }) }) }) }));
    };
    return receivedInvitationFor !== '' ? renderModal() : null;
};
_react_refresh_temp_2 = BreakoutRoomInvitation;
$RefreshReg$(_react_refresh_temp_2, "BreakoutRoomInvitation");
_react_refresh_temp_4(BreakoutRoomInvitation, "cj3sgukNpZCyVy4VkDH+Tu1Kg2s=", false, () => [useTranslation, useAppDispatch, useAppSelector, useJoinRoomMutation]);
export default _react_refresh_temp_3 = React.memo(BreakoutRoomInvitation);
$RefreshReg$(_react_refresh_temp_3, "%default%");
